import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DefaultLayout from '../../layout/DefaultLayout';
import Loading from '../../components/Loading';
import Alert from '../../components/Alert';
import paymentService from '../../services/paymentService';
import { useGlobalState } from '../../state';
import { toCurrency } from '../../utils/formaters';
import CustomPicker from '../../components/CustomPicker';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import Breadcrumb from '../../components/Breadcrumbs/Breadcrumb';

const abaoAnticipado = 135;

const validationSchema = yup.object().shape({
    mes: yup.string().when(
        ['$selectedConcepto'],
        ([selectedMes, selectedConcepto], schema) => {
            if (selectedConcepto && parseInt(selectedConcepto) == abaoAnticipado)
                return schema.notRequired();
            else
                if (selectedMes)
                    return schema.notRequired();
                else
                    return schema.required("El mes es requerido");
        }
    ),
    anio: yup.string().when(
        ['$selectedConcepto'],
        ([selectedAnio, selectedConcepto], schema) => {
            if (selectedConcepto && parseInt(selectedConcepto) == abaoAnticipado)
                return schema.notRequired();
            else
                if (selectedAnio)
                    return schema.notRequired();
                else
                    return schema.required("El año es requerido");
        }
    ),
    concepto: yup.string().when(
        ['$selectedConcepto'],
        ([selectedConcepto], schema) => {
            if (selectedConcepto)
                return schema.notRequired();
            else
                return schema.required("El Concepto es requerido");
        }
    ),
    monto: yup
        .number()
        .when(
            ['$selectedConcepto'],
            ([selectedConcepto], schema) => {
                if (selectedConcepto && parseInt(selectedConcepto) == abaoAnticipado) {
                    // No requerido, puede ser null o vacío
                    return schema.notRequired();
                } else {
                    // Requerido, positivo y numérico
                    return schema.required("El monto es requerido").positive("El monto debe ser un número positivo").typeError("El monto debe ser un número");
                }
            }
        ),
});

const STORAGE_KEY = 'pagoTemporal';

const Boleta: React.FC = () => {
  const navigate = useNavigate();
  const [user] = useGlobalState('user');
  const [loading, setLoading] = useState<boolean>(false);
  const [pagoEnviado, setPagoEnviado] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [anios, setAnios] = useState<any[]>([]);
  const [conceptos, setConceptos] = useState<any[]>([]);
  const [pagoTemporal, setPagoTemporal] = useState<any | undefined>(undefined);
  const [selectedConcepto, setSelectedConcepto] = useState<string | undefined>();
  const [selectedMes, setSelectedMes] = useState<string | undefined>();
  const [selectedAnio, setSelectedAnio] = useState<string | undefined>();
  const [displayMonto, setDisplayMonto] = useState<string>('0,00');

  const { control, handleSubmit, formState: { errors }, watch, setValue } = useForm({
    resolver: yupResolver(validationSchema),
    context: { selectedConcepto },
    defaultValues: { monto: 0, mes: '', anio: '', concepto: '' }
  });

  // Mantener display sincronizado cuando cambie el valor numérico del formulario
  useEffect(() => {
    const monto = watch('monto');
    const numeric = typeof monto === 'number' && !isNaN(monto) ? monto : 0;
    const formatted = new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numeric);
    setDisplayMonto(formatted);
  }, [watch]);

  const formatNumberEs = (n: number) =>
    new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

  const handleMontoChange = (raw: string) => {
    // Mantener solo dígitos
    const digits = raw.replace(/\D/g, '');
    // Interpretar como centavos (últimos 2 dígitos decimales)
    const centsInt = digits === '' ? 0 : parseInt(digits, 10);
    const numeric = centsInt / 100;

    // Actualizar formulario (numérico) y visualización
    setValue('monto', numeric, { shouldValidate: true, shouldDirty: true });
    setDisplayMonto("$" + formatNumberEs(numeric));
  };

  const handleMontoFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.setSelectionRange(e.target.value.length, e.target.value.length);
  };

  useEffect(() => {
    // wait for auth check
    if (user === undefined) return;
    if (!user) {
      navigate('/auth/signin');
      return;
    }

    setLoading(true);
    // build years list
    const hoy = new Date().getFullYear();
    const years: any[] = [];
    for (let i = 0; i < 15; i++) years.push({ label: (hoy - i).toString(), value: (hoy - i).toString() });
    setAnios(years);

    // fetch conceptos
    paymentService.getConceptosBoleta().then((result: any) => {
      if (result && result.success) {
        setConceptos((result.data || []).map((x: any) => ({ label: x.descripcion, value: String(x.id) })));
      } else {
        setError(result?.errors?.[0] ?? 'Ocurrió un error al obtener conceptos');
      }
    }).catch((ex: any) => setError(ex?.message ?? 'Ocurrió un error'))
      .finally(() => setLoading(false));
  }, [user, navigate]);

  useEffect(() => {
    if (!pagoTemporal) return;
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(pagoTemporal)); } catch {}
    if (pagoTemporal.monto === pagoTemporal.montoOriginal) {
      setPagoEnviado(true);
      if (pagoTemporal.url) window.open(pagoTemporal.url, '_blank', 'noopener');
    }
  }, [pagoTemporal]);

  const onSubmit = async (data: any) => {
    setLoading(true);
    setError(undefined);

    if (!user) {
      setError('Usuario no identificado');
      setLoading(false);
      return;
    }

    if (!selectedConcepto) {
      setError('Seleccione un concepto');
      setLoading(false);
      return;
    }

    const concepto = conceptos.find(c => c.value === selectedConcepto);
    const mes = selectedMes ? selectedMes : undefined;
    const anio = selectedAnio ? selectedAnio : undefined;

    // Validate required period except for abao anticipado
    if (parseInt(selectedConcepto) !== abaoAnticipado && (!mes || !anio)) {
      setError('Periodo incompleto');
      setLoading(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('TipoPago', String('MERCADOPAGO_BOLETA'));

      if (anio && mes) {
        formData.append('Periodo', `${anio}${mes}`);
        formData.append('Monto', String(Number(data.monto ?? 0)));
      } else {
        // abao anticipado
        formData.append('Monto', '1');
        formData.append('Periodo', '');
      }

      formData.append('Concepto', String(selectedConcepto));
      formData.append('Metadata', JSON.stringify({ descripcion: concepto?.label ?? '' }));

      // Callbacks for success/failure
      const baseUrl = window.location.origin;
      formData.append('URLCallBackExito', `${baseUrl}/pagos/callbackPagoBoletaExito`);
      formData.append('URLCallBackFalla', `${baseUrl}/pagos/callbackPagoBoletaFalla`);

      const response = await paymentService.crearPago(formData);
      setLoading(false);
      if (response && response.success) {
        response.data.montoOriginal = data.monto;
        response.data.periodo = anio && mes ? `${anio}${mes}` : '';
        response.data.concepto = String(selectedConcepto);
        response.data.metadata = { descripcion: concepto?.label ?? '' };
        setPagoTemporal(response.data);
      } else {
        setError(response?.errors?.[0] ?? 'Ocurrió un error al generar el pago');
      }
    } catch (ex: any) {
      setLoading(false);
      setError(ex?.message ?? 'Ocurrió un error inesperado');
    }
  };

  const abrirMercadoPago = () => {
    if (!pagoTemporal) return;
    setPagoEnviado(true);
    if (pagoTemporal.url) window.open(pagoTemporal.url, '_blank', 'noopener');
  };

  return (
      <DefaultLayout>
          <Breadcrumb pageName="Pagar Boleta" />

      <div className="p-4">
          <div className="md:max-w-6xl mx-auto bg-white md:p-6 rounded">

          {error && <Alert type="failure" message={error} />}


          {loading && <Loading statusText="Cargando" />}         

          {!pagoEnviado && !pagoTemporal &&
            <div className="md:p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">Concepto</label>
                <Controller
                  control={control}
                  name="concepto"
                  render={() => (
                    <CustomPicker
                      options={conceptos}
                      onValueChange={(v: string | number) => setSelectedConcepto(String(v))}
                      selectedValue={selectedConcepto}
                      placeholder="Seleccione una opción"
                    />
                  )}
                />
                {errors.concepto && <p className="text-red-600 text-sm mt-1">{(errors.concepto as any).message}</p>}
              </div>

              {selectedConcepto && parseInt(selectedConcepto) !== abaoAnticipado &&
                <>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700">Periodo a pagar</label>
                    <div className="flex gap-3 mt-2">
                      <div className="flex-1">
                        <CustomPicker
                          options={[ /* months list same shape as Messages.periodos */ { label: '01', value: '01' }, { label: '02', value: '02' }, { label: '03', value: '03' }, { label: '04', value: '04' }, { label: '05', value: '05' }, { label: '06', value: '06' }, { label: '07', value: '07' }, { label: '08', value: '08' }, { label: '09', value: '09' }, { label: '10', value: '10' }, { label: '11', value: '11' }, { label: '12', value: '12' } ]}
                          onValueChange={(v: string | number) => setSelectedMes(String(v))}
                          selectedValue={selectedMes}
                          placeholder="Mes..."
                        />
                        {errors.mes && <p className="text-red-600 text-sm mt-1">{(errors.mes as any).message}</p>}
                      </div>

                      <div className="flex-1">
                        <CustomPicker
                          options={anios}
                          onValueChange={(value: string | number) => setSelectedAnio(String(value))}
                          selectedValue={selectedAnio}
                          placeholder="Año..."
                        />
                        {errors.anio && <p className="text-red-600 text-sm mt-1">{(errors.anio as any).message}</p>}
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">Monto a pagar</label>
                    <Controller
                      control={control}
                      name="monto"
                      render={({ field: { value } }) => (
                        <input
                          type="text"
                          inputMode="numeric"
                          value={displayMonto}
                          onChange={(e) => handleMontoChange(e.target.value)}
                          onFocus={handleMontoFocus}
                          className={`mt-1 block w-full rounded-md border text-3xl ${errors.monto ? 'border-red-500' : 'border-gray-300'} p-2`}
                          placeholder="0,00"
                        />
                      )}
                    />
                    {errors.monto && <p className="text-red-600 text-sm mt-1">{(errors.monto as any).message}</p>}
                  </div>
                </>
              }

              <div className="mt-6 text-center">
                <button
                  onClick={handleSubmit(onSubmit)}
                  disabled={loading}
                  className={`px-6 py-3 rounded font-semibold text-white ${loading ? 'bg-gray-400' : 'bg-primario-celeste-800'}`}
                >
                  {loading ? 'Procesando...' : 'Pagar'}
                </button>
              </div>
            </div>
          }

          {pagoEnviado && pagoTemporal &&
            <div className="mt-6">
              <p className="text-sm">Has sido redirigido a Mercado Pago!</p>
              <div className="mt-6">
                <button onClick={() => navigate('/')} className="px-6 py-3 rounded font-semibold text-white bg-primario-celeste-800">Volver</button>
              </div>
            </div>
          }

          {!pagoEnviado && pagoTemporal && pagoTemporal.monto !== pagoTemporal.montoOriginal && selectedConcepto &&
            <div className="mt-6 text-center">
              <h3 className="text-xl font-semibold mb-2">Confirmar monto a pagar:</h3>
              {parseInt(selectedConcepto) !== abaoAnticipado && pagoTemporal.montoOriginal !== pagoTemporal.monto &&
                <p className="text-sm text-gray-600 mb-4">Se han sumado intereses por deuda pendiente y el monto a pagar es de:</p>
              }
              <div className="text-4xl font-extrabold text-primario-celeste-800 mb-6">{toCurrency(pagoTemporal.monto)}</div>
              <button onClick={abrirMercadoPago} disabled={loading} className="px-6 py-3 rounded font-semibold text-white bg-primario-celeste-800">Confirmar</button>
            </div>
          }            
            </div>

            <div className="mt-6 flex justify-center gap-3">
                <button
                    onClick={() => navigate(-1)}
                    className="px-4 py-2 rounded border border-gray-300 bg-white"
                >
                    Volver
                </button>
            </div>
      </div>
    </DefaultLayout>
  );
};

export default Boleta;